// Iter 122 — Simple auth. No retries, no pre-warm, no heartbeats.
// One POST to /auth/login → store token in localStorage → done.
// The previous version had ~5 layers of resilience that bred bugs.
// We trust the network. If it fails, the user sees the error and retries.
//
// Iter 141 — Production hardening: random logouts every few minutes were
// reported on the live deployment.  Root cause matrix:
//   • Single transient `/auth/me` 401 (Mongo blip, ingress hiccup, pod
//     cold-start, replica with mismatched JWT_SECRET in early boot)
//     was enough to wipe the session.
// Fix: require **two consecutive 401/403s within 30s** before clearing
// the token.  A first 401 triggers a one-shot retry after 2s with a
// fresh `/auth/me` call.  If THAT also returns 401/403, we log out.
// Any 5xx / network error / 503 still leaves the token alone.
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

const AuthContext = createContext(null);
const TOKEN_KEY = "ndaku_token";

// Tunables for the two-strike 401 policy.
const AUTH_RETRY_DELAY_MS = 2000;
const AUTH_STRIKE_WINDOW_MS = 30_000;

const readToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
const writeToken = (tok) => {
  try { localStorage.setItem(TOKEN_KEY, tok); } catch {}
};
const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tracks the last unauthenticated response so a single hiccup doesn't
  // log the user out — we require a second strike within the window.
  const lastAuthFailRef = useRef(0);

  // Attach axios Authorization header globally when token is present.
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      writeToken(token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      clearToken();
    }
  }, [token]);

  const refreshMe = useCallback(async () => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    const callMe = () => axios.get(`${API}/auth/me`);
    try {
      const { data } = await callMe();
      setUser(data);
      // Successful response clears the strike counter so future blips
      // get a fresh two-strike budget.
      lastAuthFailRef.current = 0;
      return data;
    } catch (e) {
      const status = e?.response?.status;
      // Non-auth failure (network down, 5xx, 503 from backend Mongo
      // blip) — leave the token alone and bail.  The next page nav or
      // explicit refreshMe call will retry naturally.
      if (status !== 401 && status !== 403) {
        return null;
      }
      // Two-strike rule: first 401 → wait 2s, retry once.  Only kill
      // the session if BOTH calls fail with 401/403 inside the window.
      const now = Date.now();
      const prevFailAt = lastAuthFailRef.current;
      lastAuthFailRef.current = now;
      const isSecondStrike = prevFailAt && now - prevFailAt < AUTH_STRIKE_WINDOW_MS;
      if (!isSecondStrike) {
        // First strike — schedule a retry without clearing state yet.
        try {
          await new Promise((r) => setTimeout(r, AUTH_RETRY_DELAY_MS));
          const { data } = await callMe();
          setUser(data);
          lastAuthFailRef.current = 0;
          return data;
        } catch (retryErr) {
          const retryStatus = retryErr?.response?.status;
          if (retryStatus !== 401 && retryStatus !== 403) {
            // Retry hit a non-auth error — preserve the session;
            // the next render cycle will try again.
            return null;
          }
          // Second consecutive 401/403 → genuine session loss.
          setToken(null);
          setUser(null);
          return null;
        }
      }
      // Second strike inside the window — genuine session loss.
      setToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
    writeToken(data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    lastAuthFailRef.current = 0;
    return data.user;
  };

  const register = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password });
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
    writeToken(data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    lastAuthFailRef.current = 0;
    return data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    lastAuthFailRef.current = 0;
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
